/**
 * 32-card deck (7–ace), shuffle with injected RNG, deal 3-2-3.
 *
 * @module game/belot/armenian/deck
 */

const {
  SUIT_MIN,
  SUIT_MAX,
  RANK_MIN,
  RANK_MAX,
  makeCard,
  CARDS_PER_DECK,
} = require("./types");

/**
 * @returns {number[]} fresh deck, deterministic order: clubs…spades, 7…A within suit
 */
function buildDeck() {
  /** @type {number[]} */
  const deck = [];
  for (let s = SUIT_MIN; s <= SUIT_MAX; s += 1) {
    for (let r = RANK_MIN; r <= RANK_MAX; r += 1) {
      deck.push(makeCard(s, r));
    }
  }
  if (deck.length !== CARDS_PER_DECK) {
    throw new Error(`armenian/deck: expected ${CARDS_PER_DECK} cards`);
  }
  return deck;
}

/**
 * Fisher–Yates shuffle. `rng` must return independent samples in `[0, 1)`.
 *
 * @param {number[]} deck mutable array of card ids
 * @param {() => number} rng
 * @returns {number[]} same array instance
 */
function shuffle(deck, rng) {
  if (typeof rng !== "function") {
    throw new TypeError("armenian/deck.shuffle: rng must be a function");
  }
  for (let i = deck.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const t = deck[i];
    deck[i] = deck[j];
    deck[j] = t;
  }
  return deck;
}

/**
 * Deal 3-2-3 clockwise starting from seat 0 (four rounds: +3, +2, +3 per seat).
 *
 * @param {number[]} deck exactly 32 cards (typically already shuffled); consumed from the end for clarity — actually we take from front: first 12 cards are first round etc.
 * @returns {{ hands: number[][], remainder: number[] }}
 */
function deal323(deck) {
  if (!Array.isArray(deck) || deck.length !== CARDS_PER_DECK) {
    throw new Error("armenian/deck.deal323: deck must have 32 cards");
  }
  const copy = deck.slice();
  /** @type {number[][]} */
  const hands = [[], [], [], []];
  const rounds = [3, 2, 3];
  for (let ri = 0; ri < rounds.length; ri += 1) {
    const take = rounds[ri];
    for (let seat = 0; seat < 4; seat += 1) {
      for (let k = 0; k < take; k += 1) {
        const card = copy.shift();
        if (card === undefined) throw new Error("armenian/deck.deal323: underflow");
        hands[seat].push(card);
      }
    }
  }
  return { hands, remainder: copy };
}

module.exports = {
  buildDeck,
  shuffle,
  deal323,
};
